import { getPricingProducts } from "../webapp/js/pricing-config.js";
import { cloudLimitsFromProduct } from "./cloud-entitlements.js";
import { MIN_CHARGE_CENTS } from "./subscription-pricing.js";

const PAID_PLAN_ORDER = ["basic", "advanced", "pro", "enterprise"];

/**
 * Unused prepaid value, as a share of the period.
 * `days left / days in the period` is the fraction of seconds still unused.
 */
export function remainingValueCents({ amountPaidCents, periodStart, periodEnd, now }) {
  const paid = Math.max(0, Math.round(Number(amountPaidCents) || 0));
  const start = Number(periodStart);
  const end = Number(periodEnd);
  const at = Number(now);
  if (!paid || !(end > start) || !(at < end)) return 0;
  const left = Math.max(0, Math.min(end, at) - start);
  const period = end - start;
  const unused = period - left;
  return Math.round((paid * unused) / period);
}

/**
 * Pre-tax split of plan credit against one new period.
 * A remainder below Stripe's minimum charge stays as credit.
 */
export function settlePlanChange({ existingCreditCents, remainingCents, newPriceCents }) {
  const credit =
    Math.max(0, Math.round(Number(existingCreditCents) || 0)) +
    Math.max(0, Math.round(Number(remainingCents) || 0));
  const price = Math.max(0, Math.round(Number(newPriceCents) || 0));
  let applied = Math.min(credit, price);
  let amountDue = price - applied;
  if (amountDue > 0 && amountDue < MIN_CHARGE_CENTS) {
    applied -= amountDue;
    amountDue = 0;
  }
  return {
    creditBeforeInvoice: credit,
    appliedCents: applied,
    amountDueCents: amountDue,
    remainingCreditCents: credit - applied,
  };
}

export function paidPlanRank(productId) {
  return PAID_PLAN_ORDER.indexOf(productId);
}

export function changeKind({ currentProductId, targetProductId, currentFeeCents, nextFeeCents }) {
  const currentLimits = cloudLimitsFromProduct(getPricingProducts()[currentProductId]);
  const nextLimits = cloudLimitsFromProduct(getPricingProducts()[targetProductId]);
  const lowersCaps = ["maxFontsOwned", "maxGlyphsPerFont", "maxInvitesPerAsset"].some(
    (key) =>
      nextLimits[key] != null &&
      (currentLimits[key] == null || nextLimits[key] < currentLimits[key]),
  );
  if (lowersCaps) return "downgrade";
  if (Math.round(Number(nextFeeCents) || 0) > Math.round(Number(currentFeeCents) || 0)) {
    return "upgrade";
  }
  return "downgrade";
}

export function summarizeCloudUsage(rows) {
  const fonts = Array.isArray(rows) ? rows : [];
  let highestGlyphs = null;
  let highestInvitees = null;
  for (const font of fonts) {
    const name = String(font.name || "Untitled");
    const glyphCount = Math.max(0, Number(font.glyph_count) || 0);
    const pendingCount = Math.max(0, Number(font.pending_count) || 0);
    const inviteeCount =
      Math.max(0, Number(font.member_count) || 0) + pendingCount;
    const glyphCandidate = { name, glyphCount };
    const inviteeCandidate = { name, inviteeCount, pendingCount };
    if (glyphCount > 0) {
      highestGlyphs = preferHigher(highestGlyphs, glyphCandidate, "glyphCount");
    }
    if (inviteeCount > 0) {
      highestInvitees = preferHigher(
        highestInvitees,
        inviteeCandidate,
        "inviteeCount",
      );
    }
  }
  return {
    fontCount: fonts.length,
    highestGlyphs,
    highestInvitees,
  };
}

function preferHigher(current, candidate, key) {
  if (!current) return candidate;
  if (candidate[key] > current[key]) return candidate;
  if (candidate[key] === current[key] && candidate.name < current.name) return candidate;
  return current;
}

export function usageOverTarget(summary, product) {
  const limits = cloudLimitsFromProduct(product);
  const fontCap = limits.maxFontsOwned;
  const glyphCap = limits.maxGlyphsPerFont;
  const inviteeCap = limits.maxInvitesPerAsset;
  return {
    fonts: fontCap != null && (summary?.fontCount || 0) > fontCap,
    glyphs:
      glyphCap != null && (summary?.highestGlyphs?.glyphCount || 0) > glyphCap,
    invitees:
      inviteeCap != null && (summary?.highestInvitees?.inviteeCount || 0) > inviteeCap,
  };
}

export function usageFits(summary, product) {
  const over = usageOverTarget(summary, product);
  return !over.fonts && !over.glyphs && !over.invitees;
}

export async function getPlanCreditCents(db, userId) {
  const row = await db
    .prepare(`SELECT balance_cents FROM plan_credit_balances WHERE user_id = ?`)
    .bind(userId)
    .first();
  return Math.max(0, Number(row?.balance_cents) || 0);
}

export async function setPlanCreditCents(db, userId, cents, now) {
  const balance = Math.max(0, Math.round(Number(cents) || 0));
  await db
    .prepare(
      `INSERT INTO plan_credit_balances (user_id, balance_cents, updated_at)
       VALUES (?, ?, ?)
       ON CONFLICT(user_id) DO UPDATE SET
         balance_cents = excluded.balance_cents,
         updated_at = excluded.updated_at`,
    )
    .bind(userId, balance, now)
    .run();
  return balance;
}

export async function consumePlanCreditCents(db, userId, consumed, now) {
  const amount = Math.max(0, Math.round(Number(consumed) || 0));
  if (!amount) return getPlanCreditCents(db, userId);
  const current = await getPlanCreditCents(db, userId);
  return setPlanCreditCents(db, userId, current - amount, now);
}

export function creditCentsFromStripeBalance(balance) {
  const signed = Number(balance);
  if (!Number.isFinite(signed) || signed >= 0) return 0;
  return -signed;
}

export function invoiceSettlesPlanCredit(invoice) {
  return invoice?.billing_reason !== "subscription_update";
}

function inviteeSummary(font) {
  const count = Number(font?.inviteeCount) || 0;
  const pending = Number(font?.pendingCount) || 0;
  if (pending > 0) {
    const label = pending === 1 ? "open invitation" : "open invitations";
    return `${font.name} (${count}, ${pending} ${label})`;
  }
  return `${font.name} (${count})`;
}

export function cloudFontAccountRows(usage) {
  const fontCount = Number(usage?.fontCount) || 0;
  const rows = [{ label: "Fonts", value: String(fontCount) }];
  if (fontCount > 0 && usage?.highestGlyphs) {
    rows.push({
      label: "Most glyphs",
      value: `${usage.highestGlyphs.name} (${Number(usage.highestGlyphs.glyphCount) || 0})`,
    });
  }
  if (fontCount > 0 && usage?.highestInvitees) {
    rows.push({
      label: "Most invitees",
      value: inviteeSummary(usage.highestInvitees),
    });
  }
  return rows;
}

export function planCreditConsumedCents(invoice) {
  const start = invoice?.starting_balance;
  const end = invoice?.ending_balance;
  if (typeof start !== "number" || typeof end !== "number") return 0;
  return Math.max(0, end - start);
}
